import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Format options offered per data type. Values are the unit codes understood by
// @/lib/units (mirrors backend explore_advanced constants).
const UNIT_OPTIONS = [
  { value: 'none', label: 'Number (plain)' },
  { value: 'USD', label: 'Currency ($)' },
  { value: 'USD_thousands', label: 'Currency (thousands, $K)' },
  { value: 'USD_millions', label: 'Currency (millions, $M)' },
  { value: 'percent', label: 'Percentage (0–100 → %)' },
  { value: 'percent_decimal', label: 'Percentage (decimal ratio → %)' },
  { value: 'ratio', label: 'Ratio (×)' },
  { value: 'count', label: 'Count' },
  { value: 'days', label: 'Days' },
];
const NUMERIC_TYPES = ['float', 'int', 'integer', 'number', 'decimal', 'numeric', 'double', 'money', 'bigint', 'real'];
const isNumericField = (a) => NUMERIC_TYPES.includes(String(a?.data_type || '').toLowerCase()) || String(a?.semantic_type || '') === 'measure';

// Phase 1 Quick-Edit: focused edits (Display Name, synonyms, description) to a
// single model field without leaving AI Explore. "Open full model" promotes to
// the full editor. On save, the caller badges dependent cards to re-run.
export default function QuickEditFieldDrawer({ open, onOpenChange, modelId, fields = [], initialField, onSaved }) {
  const [entities, setEntities] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(initialField || (fields[0]?.name || ''));
  const [displayName, setDisplayName] = useState('');
  const [synonyms, setSynonyms] = useState('');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState('');

  useEffect(() => { if (open) { setSel(initialField || (fields[0]?.name || '')); loadEntities(); }   }, [open, initialField]);

  const loadEntities = async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const res = await api.getSemanticEntities(modelId);
      setEntities(Array.isArray(res) ? res : (res?.entities || []));
    } catch { /* leave empty */ }
    try {
      const m = await api.getSemanticMeasures(modelId);
      setMeasures(Array.isArray(m) ? m : (m?.measures || []));
    } catch { /* leave empty */ }
    setLoading(false);
  };

  // Locate the entity + attribute for the selected field name.
  const locate = (name) => {
    for (const e of entities) {
      const idx = (e.attributes || []).findIndex(a => a.name === name);
      if (idx >= 0) return { entity: e, idx, attr: e.attributes[idx] };
    }
    return null;
  };

  // A calculated field is a semantic MEASURE (stored separately from entity
  // attributes) — locate it by name/business_name so it can be quick-edited too.
  const locateMeasure = (name) => (measures || []).find(
    m => m.name === name || m.business_name === name || m.display_name === name
  ) || null;

  useEffect(() => {
    const loc = locate(sel);
    const a = loc?.attr || locateMeasure(sel) || {};
    setDisplayName(a.business_name || a.display_name || '');
    setSynonyms((a.synonyms && a.synonyms.length) ? a.synonyms.join(', ') : '');
    setDescription(a.description || '');
    setUnit(a.unit || a.format || 'none');
  }, [sel, entities, measures]); // eslint-disable-line

  const save = async () => {
    const loc = locate(sel);
    const meas = loc ? null : locateMeasure(sel);
    if (!loc && !meas) { toast.error('Field not found in the model'); return; }
    const dn = displayName.trim();
    setSaving(true);
    try {
      if (meas) {
        // Calculated field → update the semantic measure directly.
        await api.updateSemanticMeasure(meas.measure_id || meas.id, {
          business_name: dn,
          synonyms: synonyms.split(',').map(s => s.trim()).filter(Boolean),
          description: description.trim(),
          ...(unit && unit !== 'none' ? { unit, format: unit } : {}),
        });
      } else {
        if (dn && (loc.entity.attributes || []).some((a, i) => i !== loc.idx && ((a.business_name || '').trim().toLowerCase() === dn.toLowerCase()))) {
          toast.error(`Display Name "${dn}" is already used by another field in this table`);
          setSaving(false);
          return;
        }
        const attrs = [...loc.entity.attributes];
        attrs[loc.idx] = {
          ...attrs[loc.idx],
          business_name: dn,
          synonyms: synonyms.split(',').map(s => s.trim()).filter(Boolean),
          description: description.trim(),
          ...(isNumericField(loc.attr) ? { unit: unit === 'none' ? '' : unit } : {}),
        };
        await api.updateSemanticEntity(loc.entity.entity_id, { attributes: attrs });
      }
      toast.success('Field updated — re-run affected results to apply');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.message || 'Could not save'); }
    setSaving(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[420px] sm:max-w-[420px] overflow-y-auto" data-testid="quick-edit-field-drawer">
        <SheetHeader>
          <SheetTitle className="text-base">Quick edit field</SheetTitle>
        </SheetHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading model…</div>
        ) : (
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs text-muted-foreground">Field</Label>
              <Select value={sel} onValueChange={setSel}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="quick-edit-field-picker"><SelectValue placeholder="Select a field" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {fields.map(f => <SelectItem key={f.name} value={f.name}>{f.label || f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Display Name</Label>
              <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. Written Premium" className="mt-1 text-sm" data-testid="quick-edit-display-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Synonyms (comma-separated)</Label>
              <Input value={synonyms} onChange={e => setSynonyms(e.target.value)} placeholder="e.g. state, region" className="mt-1 text-sm" data-testid="quick-edit-synonyms" />
              <p className="text-[10px] text-muted-foreground/80 mt-1">Other words users type for this field — resolves in AI Explore.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="What this field represents" className="mt-1 text-sm" data-testid="quick-edit-description" />
            </div>
            {(isNumericField(locate(sel)?.attr || {}) || !!locateMeasure(sel)) && (
              <div>
                <Label className="text-xs text-muted-foreground">Format</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger className="mt-1 h-9 text-sm" data-testid="quick-edit-format"><SelectValue placeholder="Number" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {UNIT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground/80 mt-1">How this measure's values display in AI Explore (e.g. a ratio shown as a percentage).</p>
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={save} disabled={saving || !sel} className="flex-1 text-sm" data-testid="quick-edit-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
              <Button variant="outline" onClick={() => { onOpenChange(false); window.open(`/semantic-models?model=${modelId}`, '_blank'); }} className="text-sm" title="Open the full model editor in a new tab" data-testid="quick-edit-open-full">
                <ExternalLink className="w-4 h-4 mr-1" /> Full model
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
